
import React, { useState, useEffect, useMemo } from "react";
import { useLeads, Lead } from "@/context/LeadContext";
import { usePartners } from "@/context/PartnerContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { phpApi } from "@/integrations/php/client";
import { sendSMS } from "@/utils/smsUtils";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { el } from "date-fns/locale";
import {
  MessageSquare,
  Mail,
  Users,
  Send,
  Clock3,
  RefreshCw,
  Search,
  CheckCircle2,
  Eye,
  EyeOff,
  BarChart2,
  User,
  Phone,
} from "lucide-react";
import { PageHeader } from "@/components/layouts/PageHeader";
import { StatCard } from "@/components/ui/StatCard";
import UnassignedLeadNotifier from "@/components/messages/UnassignedLeadNotifier";
import SmsStatusBadge from "@/components/ui-custom/SmsStatusBadge";
import EmailEventBadge from "@/components/ui-custom/EmailEventBadge";

// ── Constants ─────────────────────────────────────────────────────────────────

const SERVICE_LABELS: Record<string, string> = {
  εξοικονομώ: "Εξοικονομώ",
  αντλίεςΘερμότητας: "Αντλίες",
  φωτοβολταϊκά: "Φ/Β",
  ανακαινίζωΕνεργειακά: "Ανακαινίζω · Ενεργειακά",
  ανακαινίζωΠλήρηςΑνακαίνιση: "Ανακαινίζω · Ανακαίνιση",
  θέρμανση: "Θέρμανση",
  κλιματισμός: "Κλιματισμός",
  μόνωση: "Μόνωση",
  θερμομόνωση: "Θερμομόνωση",
};

const STAGE_COLORS: Record<string, string> = {
  "Νέο": "bg-purple-100 text-purple-700 border-purple-200",
  "σεΕκκρεμότητα": "bg-amber-100 text-amber-700 border-amber-200",
  "ήθελεΠληροφορίες": "bg-blue-100 text-blue-700 border-blue-200",
  "έκλεισεΗΔουλειά": "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const STAGE_LABELS: Record<string, string> = {
  "Νέο": "Νέο",
  "σεΕκκρεμότητα": "Εκκρεμεί",
  "ήθελεΠληροφορίες": "Πληροφορίες",
  "έκλεισεΗΔουλειά": "Έκλεισε",
};

// ── Interfaces ────────────────────────────────────────────────────────────────

interface SmsLog {
  id: string;
  sms_id: string;
  status: string;
  partner_id?: string;
  lead_id?: string;
  timestamp: string;
  created_at?: string;
}

interface EmailLog {
  id: string;
  tracking_id?: string;
  lead_id?: string;
  partner_id?: string;
  email?: string;
  event?: string;
  timestamp: string;
  created_at?: string;
}

// ── SMS grouped list ──────────────────────────────────────────────────────────

interface SmsGroupedListProps {
  logs: SmsLog[];
  leads: Lead[];
  partners: { id: string; name: string; phone?: string }[];
  safeDate: (d?: string) => Date | null;
  resendingId: string | null;
  onResend: (s: SmsLog) => void;
  onOpen: (leadId: string) => void;
}

const SmsGroupedList: React.FC<SmsGroupedListProps> = ({
  logs, leads, partners, safeDate, resendingId, onResend, onOpen,
}) => {
  const todayStr = new Date().toISOString().slice(0, 10);
  const yesterdayStr = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);

  const groups: { label: string; items: SmsLog[] }[] = [
    { label: "Σήμερα", items: [] },
    { label: "Χθες", items: [] },
    { label: "Παλαιότερα", items: [] },
  ];

  logs.forEach((s) => {
    const dateStr = (s.created_at || s.timestamp || "").slice(0, 10);
    if (dateStr === todayStr) groups[0].items.push(s);
    else if (dateStr === yesterdayStr) groups[1].items.push(s);
    else groups[2].items.push(s);
  });

  const findLead = (id?: string) => leads.find((l) => l.id === id);
  const partnerInfo = (id?: string) => {
    const p = partners.find((p) => p.id === id);
    return p ? { name: p.name, phone: p.phone } : { name: "—", phone: undefined };
  };

  return (
    <div className="space-y-4 max-h-[580px] overflow-y-auto pr-1">
      {groups.map((group) => {
        if (group.items.length === 0) return null;
        return (
          <div key={group.label}>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 px-1">
              {group.label} ({group.items.length})
            </p>
            <div className="space-y-2">
              {group.items.map((s) => {
                const d = safeDate(s.created_at || s.timestamp);
                const partner = partnerInfo(s.partner_id);
                const lead = findLead(s.lead_id);
                const serviceLabel = lead?.service ? (SERVICE_LABELS[lead.service] ?? lead.service) : null;
                const isUrgent = lead?.timeframe === "άμεσα";
                return (
                  <div
                    key={s.id}
                    className="p-3 rounded-xl border border-slate-100 hover:bg-slate-50"
                  >
                    {/* Γραμμή 1 — Lead info + status + actions */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="font-medium text-slate-900 text-sm truncate">
                          {lead?.name ?? "—"}
                        </p>
                        {serviceLabel && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-slate-300 text-slate-600 shrink-0">
                            {serviceLabel}
                          </Badge>
                        )}
                        {isUrgent && (
                          <Badge className="text-[10px] px-1.5 py-0 h-4 bg-rose-100 text-rose-700 border border-rose-200 shrink-0">
                            Άμεσα
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {/* `title` is not a Lucide prop, so no tooltip ever appeared.
                            Wrapping in a span gives the native tooltip it was after. */}
                        {lead ? (
                          lead.viewed
                            ? <span title="Προβλήθηκε" className="inline-flex"><Eye className="h-4 w-4 text-emerald-500" /></span>
                            : <span title="Δεν προβλήθηκε" className="inline-flex"><EyeOff className="h-4 w-4 text-slate-300" /></span>
                        ) : null}
                        <SmsStatusBadge status={s.status} />
                        {s.status === "failed" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-amber-200 text-amber-700 hover:bg-amber-50"
                            disabled={resendingId === s.id}
                            onClick={() => onResend(s)}
                          >
                            {resendingId === s.id ? "…" : "Ξαναστείλε"}
                          </Button>
                        )}
                        {s.lead_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => onOpen(s.lead_id!)}
                          >
                            →
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* Γραμμή 2 — Συνεργάτης + timestamp */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3 text-slate-400" />
                          {partner.name}
                        </span>
                        {partner.phone && (
                          <a
                            href={`tel:${partner.phone}`}
                            className="flex items-center gap-1 text-blue-600 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Phone className="h-3 w-3" />
                            {partner.phone}
                          </a>
                        )}
                      </div>
                      <span className="text-xs text-slate-400">
                        {d ? formatDistanceToNow(d, { locale: el, addSuffix: true }) : "—"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Email grouped list ────────────────────────────────────────────────────────

interface EmailGroupedListProps {
  logs: EmailLog[];
  leads: Lead[];
  partners: { id: string; name: string }[];
  safeDate: (d?: string) => Date | null;
  onOpen: (leadId: string) => void;
}

const EmailGroupedList: React.FC<EmailGroupedListProps> = ({
  logs, leads, partners, safeDate, onOpen,
}) => {
  const todayStr = new Date().toISOString().slice(0, 10);
  const yesterdayStr = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);

  const groups: { label: string; items: EmailLog[] }[] = [
    { label: "Σήμερα", items: [] },
    { label: "Χθες", items: [] },
    { label: "Παλαιότερα", items: [] },
  ];

  logs.forEach((e) => {
    const dateStr = (e.timestamp || e.created_at || "").slice(0, 10);
    if (dateStr === todayStr) groups[0].items.push(e);
    else if (dateStr === yesterdayStr) groups[1].items.push(e);
    else groups[2].items.push(e);
  });

  // Lookup maps rather than .find() per row. These run for every rendered row
  // and, in the page-level filters, on every keystroke — a linear scan of ~2.700
  // leads per row added up to well over a million comparisons per character.
  const leadNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const l of leads) m.set(l.id, l.name);
    return m;
  }, [leads]);

  const partnerNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const p of partners) m.set(p.id, p.name);
    return m;
  }, [partners]);

  const leadName = (id?: string) => (id ? leadNameById.get(id) ?? "—" : "—");
  const partnerName = (id?: string) => (id ? partnerNameById.get(id) ?? "—" : "—");

  return (
    <div className="space-y-4 max-h-[560px] overflow-y-auto pr-1">
      {groups.map((group) => {
        if (group.items.length === 0) return null;
        return (
          <div key={group.label}>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 px-1">
              {group.label} ({group.items.length})
            </p>
            <div className="space-y-2">
              {group.items.map((e) => {
                const d = safeDate(e.timestamp || e.created_at);
                const pName = partnerName(e.partner_id);
                return (
                  <div
                    key={e.id}
                    className="p-3 rounded-xl border border-slate-100 hover:bg-slate-50"
                  >
                    {/* Γραμμή 1 — Lead + event badge + action */}
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-medium text-slate-900 text-sm truncate min-w-0">
                        {leadName(e.lead_id)}
                      </p>
                      <div className="flex items-center gap-2 shrink-0">
                        <EmailEventBadge event={e.event} />
                        {e.lead_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => onOpen(e.lead_id!)}
                          >
                            →
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* Γραμμή 2 — Συνεργάτης + email + timestamp */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        {pName !== "—" && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3 text-slate-400" />
                            {pName}
                          </span>
                        )}
                        {e.email && (
                          <span className="flex items-center gap-1 truncate max-w-[180px]">
                            <Mail className="h-3 w-3 text-slate-400 shrink-0" />
                            {e.email}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-400 shrink-0">
                        {d ? formatDistanceToNow(d, { locale: el, addSuffix: true }) : "—"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const Messages = () => {
  const { leads, refreshLeads } = useLeads();
  const { partners } = usePartners();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState("unassigned");
  const [refreshing, setRefreshing] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [sendingFollowUpId, setSendingFollowUpId] = useState<string | null>(null);

  // SMS logs state
  const [smsLogs, setSmsLogs] = useState<SmsLog[]>([]);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsSearch, setSmsSearch] = useState("");
  const [smsStatusFilter, setSmsStatusFilter] = useState("all");
  const [smsPartnerFilter, setSmsPartnerFilter] = useState("all");

  // Email logs state
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSearch, setEmailSearch] = useState("");
  const [emailEventFilter, setEmailEventFilter] = useState("all");
  const [emailPartnerFilter, setEmailPartnerFilter] = useState("all");

  // ── Computed stats ──────────────────────────────────────────────────────────

  const unassignedCount = leads.filter((l) => !l.partnerId).length;

  const today = new Date().toISOString().slice(0, 10);
  const smsTodayCount = smsLogs.filter((s) =>
    (s.created_at || s.timestamp || "").startsWith(today)
  ).length;
  const emailOpenTodayCount = emailLogs.filter(
    (e) => e.event === "open" && (e.timestamp || e.created_at || "").startsWith(today)
  ).length;
  const smsFailedCount = smsLogs.filter((s) => s.status === "failed").length;

  // SMS view rate: leads that received an SMS AND viewed the link
  const leadsWithSms = leads.filter(
    (l) => l.smsStatus === "sent" || l.smsStatus === "delivered"
  );
  const smsViewedCount = leadsWithSms.filter((l) => l.viewed).length;
  const smsViewRate =
    leadsWithSms.length > 0
      ? Math.round((smsViewedCount / leadsWithSms.length) * 100)
      : 0;

  // Follow-up: leads with unread SMS ≥ 12h
  const followUpLeads = useMemo(() => {
    return leads
      .filter(
        (l) =>
          (l.smsStatus === "sent" || l.smsStatus === "delivered") && !l.viewed
      )
      .map((l) => ({
        lead: l,
        hours: Math.floor(
          (Date.now() - new Date(l.updatedAt).getTime()) / 3_600_000
        ),
      }))
      .filter(({ hours }) => hours >= 12)
      .sort((a, b) => {
        // Urgent first, then by hours desc
        const aUrgent = a.lead.timeframe === "άμεσα" ? 1 : 0;
        const bUrgent = b.lead.timeframe === "άμεσα" ? 1 : 0;
        if (bUrgent !== aUrgent) return bUrgent - aUrgent;
        return b.hours - a.hours;
      })
      .slice(0, 30);
  }, [leads]);

  // ── Data fetching ───────────────────────────────────────────────────────────

  // These tables are large — ~2.200 SMS and ~13.000 email rows — so the log is
  // always a recent slice. Two things follow:
  //   * the slice size is stated in the UI, because "Δεν υπάρχουν εγγραφές" while
  //     silently searching 2% of the table reads as "no email was ever sent";
  //   * filtering by partner is pushed to the server, so picking a partner
  //     searches all of their history instead of whatever happens to be in the
  //     most recent page.
  const LOG_LIMIT = 500;

  const fetchSmsLogs = async (partnerId: string = smsPartnerFilter) => {
    setSmsLoading(true);
    try {
      let q = phpApi.from("sms_delivery_status").select("*");
      if (partnerId !== "all") q = q.eq("partner_id", partnerId);
      const { data } = await q.order("created_at", { ascending: false }).limit(LOG_LIMIT);
      if (data) setSmsLogs(data as SmsLog[]);
    } finally {
      setSmsLoading(false);
    }
  };

  const fetchEmailLogs = async (partnerId: string = emailPartnerFilter) => {
    setEmailLoading(true);
    try {
      let q = phpApi.from("email_logs").select("*");
      if (partnerId !== "all") q = q.eq("partner_id", partnerId);
      const { data } = await q.order("timestamp", { ascending: false }).limit(LOG_LIMIT);
      if (data) setEmailLogs(data as EmailLog[]);
    } finally {
      setEmailLoading(false);
    }
  };

  useEffect(() => {
    fetchSmsLogs(smsPartnerFilter);
  }, [smsPartnerFilter]);

  useEffect(() => {
    fetchEmailLogs(emailPartnerFilter);
  }, [emailPartnerFilter]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([refreshLeads(), fetchSmsLogs(), fetchEmailLogs()]);
    setRefreshing(false);
    toast({ title: "Ανανέωση", description: "Τα δεδομένα ενημερώθηκαν." });
  };

  const handleResendSms = async (s: SmsLog) => {
    const partner = partners.find((p) => p.id === s.partner_id);
    if (!partner?.phone) {
      toast({ title: "Δεν βρέθηκε τηλέφωνο συνεργάτη", variant: "destructive" });
      return;
    }
    setResendingId(s.id);
    try {
      const text = `Έχετε νέο ενδιαφερόμενο! Δείτε: ${window.location.origin}/lead/${s.lead_id}`;
      await sendSMS(partner.phone, text, s.lead_id ?? undefined, s.partner_id ?? undefined);
      toast({ title: "SMS εστάλη ξανά", description: `Προς ${partner.name}` });
      fetchSmsLogs();
    } catch {
      toast({ title: "Αποτυχία αποστολής", variant: "destructive" });
    } finally {
      setResendingId(null);
    }
  };

  const handleFollowUpSms = async (lead: Lead) => {
    const partner = partners.find((p) => p.id === lead.partnerId);
    if (!partner?.phone) {
      toast({ title: "Δεν βρέθηκε τηλέφωνο συνεργάτη", variant: "destructive" });
      return;
    }
    setSendingFollowUpId(lead.id);
    try {
      const text = `Υπενθύμιση: Έχετε ενδιαφερόμενο που αναμένει! Δείτε: ${window.location.origin}/lead/${lead.id}`;
      await sendSMS(partner.phone, text, lead.id, lead.partnerId ?? undefined);
      toast({ title: "SMS υπενθύμισης εστάλη", description: `Προς ${partner.name}` });
      fetchSmsLogs();
    } catch {
      toast({ title: "Αποτυχία αποστολής", variant: "destructive" });
    } finally {
      setSendingFollowUpId(null);
    }
  };

  // Lookup maps rather than .find() per row. These run for every rendered row
  // and, in the page-level filters, on every keystroke — a linear scan of ~2.700
  // leads per row added up to well over a million comparisons per character.
  const leadNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const l of leads) m.set(l.id, l.name);
    return m;
  }, [leads]);

  const partnerNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const p of partners) m.set(p.id, p.name);
    return m;
  }, [partners]);

  const leadName = (id?: string) => (id ? leadNameById.get(id) ?? "—" : "—");
  const partnerName = (id?: string) => (id ? partnerNameById.get(id) ?? "—" : "—");

  // ── Filtering ───────────────────────────────────────────────────────────────

  const filteredSms = useMemo(() => {
    return smsLogs.filter((s) => {
      if (smsStatusFilter !== "all" && s.status !== smsStatusFilter) return false;
      if (smsPartnerFilter !== "all" && s.partner_id !== smsPartnerFilter) return false;
      if (smsSearch.trim()) {
        const q = smsSearch.toLowerCase();
        if (
          !leadName(s.lead_id).toLowerCase().includes(q) &&
          !partnerName(s.partner_id).toLowerCase().includes(q) &&
          !(s.sms_id || "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [smsLogs, smsStatusFilter, smsPartnerFilter, smsSearch, leads, partners]);

  const filteredEmails = useMemo(() => {
    return emailLogs.filter((e) => {
      if (emailEventFilter !== "all" && e.event !== emailEventFilter) return false;
      if (emailPartnerFilter !== "all" && e.partner_id !== emailPartnerFilter) return false;
      if (emailSearch.trim()) {
        const q = emailSearch.toLowerCase();
        if (
          !leadName(e.lead_id).toLowerCase().includes(q) &&
          !partnerName(e.partner_id).toLowerCase().includes(q) &&
          !(e.email || "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [emailLogs, emailEventFilter, emailPartnerFilter, emailSearch, leads, partners]);

  const safeDate = (d?: string) => {
    if (!d) return null;
    // MySQL returns "YYYY-MM-DD HH:MM:SS" with no timezone — force UTC so JS
    // doesn't treat it as local time (which would be off by UTC+3 in Greece).
    const iso = d.includes("T") ? d : d.replace(" ", "T");
    const utc = iso.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + "Z";
    const parsed = new Date(utc);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        title="Μηνύματα"
        description="Αδιάθετα leads, αρχείο SMS/Email, follow-up λίστα και αποστολή SMS"
        icon={<MessageSquare />}
        iconGradient="blue"
        actions={
          <Button
            onClick={handleRefresh}
            variant="outline"
            disabled={refreshing}
            className="border-slate-200"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Ανανέωση
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          value={unassignedCount}
          title="Μη Ανατεθειμένα"
          icon={<Users />}
          variant="gradient"
          gradientColors="from-amber-500 to-orange-600"
          size="compact"
        />
        <StatCard
          value={smsTodayCount}
          title="SMS Σήμερα"
          icon={<Send />}
          variant="success"
          size="compact"
        />
        <StatCard
          value={emailOpenTodayCount}
          title="Email Ανοίγματα"
          icon={<Mail />}
          variant="info"
          size="compact"
        />
        <StatCard
          value={`${smsViewRate}%`}
          title={`SMS Προβλήθηκαν (${smsViewedCount}/${leadsWithSms.length})`}
          icon={<BarChart2 />}
          variant="gradient"
          gradientColors="from-violet-500 to-purple-600"
          size="compact"
        />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-lg flex-wrap h-auto gap-1">
          <TabsTrigger
            value="unassigned"
            className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Users className="h-4 w-4 mr-2" />
            Μη Ανατεθειμένα
            {unassignedCount > 0 && (
              <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-700">
                {unassignedCount}
              </Badge>
            )}
          </TabsTrigger>

          <TabsTrigger
            value="sms"
            className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            SMS Αρχείο
            {smsFailedCount > 0 && (
              <Badge variant="secondary" className="ml-2 bg-rose-100 text-rose-700">
                {smsFailedCount}
              </Badge>
            )}
          </TabsTrigger>

          <TabsTrigger
            value="email"
            className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Mail className="h-4 w-4 mr-2" />
            Email Αρχείο
          </TabsTrigger>

          <TabsTrigger
            value="followup"
            className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Clock3 className="h-4 w-4 mr-2" />
            Follow-up
            {followUpLeads.length > 0 && (
              <Badge variant="secondary" className="ml-2 bg-rose-100 text-rose-700">
                {followUpLeads.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── ΜΗ ΑΝΑΤΕΘΕΙΜΕΝΑ ── */}
        <TabsContent value="unassigned" className="mt-6">
          <UnassignedLeadNotifier leads={leads} onSuccess={() => refreshLeads()} />
        </TabsContent>

        {/* ── SMS ΑΡΧΕΙΟ ── */}
        <TabsContent value="sms" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-3 border-b border-slate-100">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={smsSearch}
                    onChange={(e) => setSmsSearch(e.target.value)}
                    placeholder="Αναζήτηση επαφής, συνεργάτη..."
                    className="pl-9 bg-slate-50 border-slate-200"
                  />
                </div>
                <Select value={smsStatusFilter} onValueChange={setSmsStatusFilter}>
                  <SelectTrigger className="w-44 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="Κατάσταση" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Όλες</SelectItem>
                    <SelectItem value="sent">Εστάλη</SelectItem>
                    <SelectItem value="delivered">Παραδόθηκε</SelectItem>
                    <SelectItem value="failed">Αποτυχία</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={smsPartnerFilter} onValueChange={setSmsPartnerFilter}>
                  <SelectTrigger className="w-52 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="Συνεργάτης" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Όλοι οι συνεργάτες</SelectItem>
                    {partners.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {smsLogs.length >= LOG_LIMIT && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                  Εμφανίζονται τα <strong>{LOG_LIMIT}</strong> πιο πρόσφατα SMS
                  {smsPartnerFilter === "all" ? " συνολικά" : " αυτού του συνεργάτη"}. Υπάρχουν και
                  παλαιότερα — φιλτράρετε ανά συνεργάτη για να δείτε το δικό του ιστορικό.
                </p>
              )}
              {smsLoading ? (
                <p className="text-sm text-slate-400 py-10 text-center">Φόρτωση...</p>
              ) : filteredSms.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-sm text-slate-500">Δεν βρέθηκαν SMS με αυτά τα κριτήρια.</p>
                  {smsLogs.length >= LOG_LIMIT && (
                    <p className="text-xs text-slate-400 mt-1">
                      Η αναζήτηση καλύπτει τα {LOG_LIMIT} πιο πρόσφατα — μπορεί να υπάρχει παλαιότερο.
                    </p>
                  )}
                </div>
              ) : (
                <SmsGroupedList
                  logs={filteredSms}
                  leads={leads}
                  partners={partners}
                  safeDate={safeDate}
                  resendingId={resendingId}
                  onResend={handleResendSms}
                  onOpen={(id) => navigate(`/admin/lead/${id}`)}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── EMAIL ΑΡΧΕΙΟ ── */}
        <TabsContent value="email" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-3 border-b border-slate-100">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={emailSearch}
                    onChange={(e) => setEmailSearch(e.target.value)}
                    placeholder="Αναζήτηση επαφής, email..."
                    className="pl-9 bg-slate-50 border-slate-200"
                  />
                </div>
                <Select value={emailEventFilter} onValueChange={setEmailEventFilter}>
                  <SelectTrigger className="w-44 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="Γεγονός" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Όλα τα γεγονότα</SelectItem>
                    <SelectItem value="sent">Εστάλη</SelectItem>
                    <SelectItem value="open">Άνοιγμα</SelectItem>
                    <SelectItem value="click">Κλικ</SelectItem>
                    <SelectItem value="bounce">Αναπήδηση</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={emailPartnerFilter} onValueChange={setEmailPartnerFilter}>
                  <SelectTrigger className="w-52 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="Συνεργάτης" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Όλοι οι συνεργάτες</SelectItem>
                    {partners.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {emailLogs.length >= LOG_LIMIT && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                  Εμφανίζονται τα <strong>{LOG_LIMIT}</strong> πιο πρόσφατα email
                  {emailPartnerFilter === "all" ? " συνολικά" : " αυτού του συνεργάτη"}. Το αρχείο
                  είναι πολύ μεγαλύτερο — φιλτράρετε ανά συνεργάτη για το δικό του ιστορικό.
                </p>
              )}
              {emailLoading ? (
                <p className="text-sm text-slate-400 py-10 text-center">Φόρτωση...</p>
              ) : filteredEmails.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-sm text-slate-500">Δεν βρέθηκαν email με αυτά τα κριτήρια.</p>
                  {emailLogs.length >= LOG_LIMIT && (
                    <p className="text-xs text-slate-400 mt-1">
                      Η αναζήτηση καλύπτει τα {LOG_LIMIT} πιο πρόσφατα — μπορεί να υπάρχει παλαιότερο.
                    </p>
                  )}
                </div>
              ) : (
                <EmailGroupedList
                  logs={filteredEmails}
                  leads={leads}
                  partners={partners}
                  safeDate={safeDate}
                  onOpen={(id) => navigate(`/admin/lead/${id}`)}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── FOLLOW-UP ── */}
        <TabsContent value="followup" className="mt-6">
          {followUpLeads.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-16 text-center">
                <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
                <p className="text-slate-700 font-medium">Δεν υπάρχουν εκκρεμή follow-ups</p>
                <p className="text-sm text-slate-400 mt-1">
                  Όλα τα μηνύματα έχουν αναγνωστεί ή είναι πρόσφατα.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                {followUpLeads.length} leads με SMS που δεν διαβάστηκαν ≥ 12 ώρες
              </p>
              {followUpLeads.map(({ lead, hours }) => {
                const serviceLabel = SERVICE_LABELS[lead.service] ?? lead.service;
                const stageLabel = STAGE_LABELS[lead.stage] ?? lead.stage;
                const stageColor = STAGE_COLORS[lead.stage] ?? "bg-slate-100 text-slate-600 border-slate-200";
                const isUrgent = lead.timeframe === "άμεσα";
                return (
                  <Card
                    key={lead.id}
                    className={`border-amber-200 bg-amber-50/40 ${isUrgent ? "border-rose-300 bg-rose-50/40" : ""}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="font-semibold text-slate-900">{lead.name}</p>
                            {isUrgent && (
                              <Badge className="text-[10px] px-1.5 py-0 h-4 bg-rose-100 text-rose-700 border border-rose-300">
                                Άμεσα
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">
                            {lead.partnerName || "Χωρίς συνεργάτη"} • {lead.phone}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5 truncate">{lead.address}</p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-slate-300 text-slate-600">
                              {serviceLabel}
                            </Badge>
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 border ${stageColor}`}>
                              {stageLabel}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <Badge
                            variant="outline"
                            className={`border-amber-300 text-amber-700 bg-amber-100 ${hours >= 48 ? "border-rose-300 text-rose-700 bg-rose-100" : ""}`}
                          >
                            {hours >= 24 ? `${Math.floor(hours / 24)}μ ${hours % 24}h` : `${hours}h`}
                          </Badge>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                              disabled={sendingFollowUpId === lead.id}
                              onClick={() => handleFollowUpSms(lead)}
                            >
                              {sendingFollowUpId === lead.id ? "…" : "Νέο SMS"}
                            </Button>
                            <Button
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => navigate(`/admin/lead/${lead.id}`)}
                            >
                              Άνοιγμα
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Messages;
